import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, ArrowRightLeft, AlertTriangle, Save } from "lucide-react";
import { hasCapability } from "@/lib/permissions";

const newItem = () => ({ id: crypto.randomUUID(), description: "", im_code: "", qty: 1, uom: "Pcs", value: "", notes: "" });

export default function MaterialTransferNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageUom = hasCapability(user, "manage_uom_master");

  const [uoms, setUoms] = useState([]);
  const [newUom, setNewUom] = useState({ code: "", label: "" });
  const [showAddUom, setShowAddUom] = useState(false);
  const [direction, setDirection] = useState("office_to_factory");
  const [transferDate, setTransferDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedBy, setExpectedBy] = useState("");
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [submitting, setSubmitting] = useState(false);

  const loadUoms = async () => {
    try { const { data } = await api.get("/uoms"); setUoms(data || []); }
    catch { /* silent */ }
  };
  useEffect(() => { loadUoms(); }, []);

  const updateItem = (id, k, v) => setItems((rows) => rows.map((r) => r.id === id ? { ...r, [k]: v } : r));
  const addItem = () => setItems((rows) => [...rows, newItem()]);
  const removeItem = (id) => setItems((rows) => rows.length > 1 ? rows.filter((r) => r.id !== id) : rows);

  const addUom = async () => {
    if (!newUom.code.trim()) return toast.error("UoM code is required");
    try {
      await api.post("/uoms", { code: newUom.code.trim(), label: newUom.label.trim() });
      toast.success(`UoM '${newUom.code}' added`);
      setNewUom({ code: "", label: "" });
      setShowAddUom(false);
      await loadUoms();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add UoM");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!purpose.trim()) return toast.error("Purpose is required");
    const cleanItems = items
      .filter((i) => i.description.trim() && Number(i.qty) > 0)
      .map((i) => ({
        description: i.description.trim(),
        im_code: i.im_code.trim() || null,
        qty: Number(i.qty),
        uom: i.uom,
        value: i.value ? Number(i.value) : null,
        notes: i.notes.trim() || null,
      }));
    if (!cleanItems.length) return toast.error("Add at least one line item");
    if (!user?.signature_data_url) {
      const ok = window.confirm("You have not saved your signature. The Transfer Note PDF will have an empty issuer signature. Continue anyway?");
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/material-transfers", {
        direction, transfer_date: transferDate,
        expected_delivery_by: expectedBy || null,
        purpose: purpose.trim(),
        items: cleanItems,
        notes: notes.trim() || null,
      });
      toast.success(`Transfer ${data.transfer_number} issued`);
      navigate(`/material-transfers/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create transfer");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="new-transfer-page">
      <button onClick={() => navigate("/material-transfers")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to Transfers
      </button>

      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">New</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
          <ArrowRightLeft className="w-7 h-7" /> Material Transfer Note
        </h1>
      </div>

      {!user?.signature_data_url && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            You haven&apos;t saved your signature yet. The Transfer Note PDF will show an empty issuer signature box.
            You can add it from the top-right avatar menu → Signature.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-base font-heading">Header</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>Direction *</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setDirection("office_to_factory")}
                        className={`flex-1 h-11 rounded-md border-2 px-3 text-sm font-medium ${direction === "office_to_factory" ? "border-[#7C1D3A] bg-[#fcf0f3] text-[#7C1D3A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        data-testid="dir-ho">
                  ↗ Office → Factory <span className="text-[10px] font-normal opacity-70 block">HO/YY-YY/xxxx</span>
                </button>
                <button type="button" onClick={() => setDirection("factory_to_office")}
                        className={`flex-1 h-11 rounded-md border-2 px-3 text-sm font-medium ${direction === "factory_to_office" ? "border-[#7C1D3A] bg-[#fcf0f3] text-[#7C1D3A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        data-testid="dir-ftr">
                  ↙ Factory → Office <span className="text-[10px] font-normal opacity-70 block">FTR/YY-YY/xxxx</span>
                </button>
              </div>
            </div>
            <div>
              <Label>Transfer Date *</Label>
              <Input type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} required data-testid="transfer-date" />
            </div>
            <div>
              <Label>Expected Delivery By</Label>
              <Input type="date" value={expectedBy} onChange={(e) => setExpectedBy(e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <Label>Purpose *</Label>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Sample dispatch, machine spare replacement..." required data-testid="transfer-purpose" />
            </div>
            <div className="md:col-span-3">
              <Label>Additional Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any handling instructions, courier details, etc." />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading">Line Items</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-line-btn">
              <Plus className="w-4 h-4 mr-1" /> Add item
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.map((it, idx) => (
              <div key={it.id} className="grid grid-cols-12 gap-2 items-start bg-slate-50 rounded-md p-3" data-testid={`line-${idx}`}>
                <div className="col-span-12 md:col-span-5">
                  <Label className="text-[10px] uppercase text-slate-500">Description *</Label>
                  <Input value={it.description} onChange={(e) => updateItem(it.id, "description", e.target.value)} placeholder="e.g. Cotton fabric roll navy 320GSM" data-testid={`line-${idx}-desc`} />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">IM Code</Label>
                  <Input value={it.im_code} onChange={(e) => updateItem(it.id, "im_code", e.target.value)} placeholder="Optional" />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label className="text-[10px] uppercase text-slate-500">Qty *</Label>
                  <Input type="number" min="0" step="0.01" value={it.qty} onChange={(e) => updateItem(it.id, "qty", e.target.value)} className="text-right" data-testid={`line-${idx}-qty`} />
                </div>
                <div className="col-span-3 md:col-span-2">
                  <Label className="text-[10px] uppercase text-slate-500">UoM *</Label>
                  <div className="flex gap-1">
                    <select value={it.uom} onChange={(e) => updateItem(it.id, "uom", e.target.value)} className="flex-1 h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid={`line-${idx}-uom`}>
                      {uoms.map((u) => <option key={u.id} value={u.code}>{u.code}</option>)}
                    </select>
                    {canManageUom && (
                      <button type="button" onClick={() => setShowAddUom(true)} title="Add new UoM"
                              className="w-10 h-10 border border-slate-200 rounded-md text-slate-500 hover:bg-slate-100" data-testid="open-add-uom-btn">
                        <Plus className="w-4 h-4 mx-auto" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-1">
                  <Label className="text-[10px] uppercase text-slate-500">Value (₹)</Label>
                  <Input type="number" min="0" step="0.01" value={it.value} onChange={(e) => updateItem(it.id, "value", e.target.value)} className="text-right" />
                </div>
                <div className="col-span-6 md:col-span-1 flex justify-end pt-6">
                  <button type="button" onClick={() => removeItem(it.id)} disabled={items.length === 1} className="text-red-500 hover:text-red-700 disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="col-span-12">
                  <Input value={it.notes} onChange={(e) => updateItem(it.id, "notes", e.target.value)} placeholder="Item notes (optional)" className="text-xs" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {showAddUom && (
          <Card className="border-[#7C1D3A]/40">
            <CardContent className="p-4 flex flex-wrap items-end gap-3">
              <div>
                <Label>New UoM Code *</Label>
                <Input value={newUom.code} onChange={(e) => setNewUom({ ...newUom, code: e.target.value })} placeholder="e.g. Sqm" className="w-28" data-testid="new-uom-code" />
              </div>
              <div>
                <Label>Label</Label>
                <Input value={newUom.label} onChange={(e) => setNewUom({ ...newUom, label: e.target.value })} placeholder="e.g. Square metre" className="w-52" />
              </div>
              <Button type="button" onClick={addUom} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-uom-btn">Save UoM</Button>
              <Button type="button" variant="outline" onClick={() => setShowAddUom(false)}>Cancel</Button>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/material-transfers")}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="submit-transfer-btn">
            <Save className="w-4 h-4 mr-2" /> {submitting ? "Creating…" : "Issue Transfer Note"}
          </Button>
        </div>
      </form>
    </div>
  );
}
